import React, { useCallback, useContext, useEffect, useState } from "react";
import "./settingsScreen.css";
import { DataLayerContext } from "../dataLayer/DataLayer";
import type { BoardFont, TextSize } from "../dataLayer/DataLayer";
import { useObservableProperty } from "../observable/useObservableProperty";
import { AppBar } from "./components/appBar";
import { PageHeading } from "./components/pageHeading";
import { Check } from "./components/icons";
import { APP_VERSION } from "../version";
import { PremiumAccessCard } from "./components/premiumAccessCard";
import { authState } from "../auth/authState";
import { displayedVersion, installationErrorMessage, installationPlugin, InstallationSite, InstallationStatus } from "../installationChannel";

interface IFontOption {
  id: BoardFont;
  name: string;
  desc: string;
  /** Uppercase specimen letters shown as mini board tiles (decorative). */
  sample: string[];
}

const FONT_OPTIONS: IFontOption[] = [
  { id: "serif", name: "Serif", desc: "The classic Wirder letters", sample: ["A", "G", "R"] },
  { id: "sans", name: "Sans-serif", desc: "Clean and modern", sample: ["A", "G", "R"] },
];

interface ITextSizeOption {
  id: TextSize;
  name: string;
  desc: string;
}

const TEXT_SIZE_OPTIONS: ITextSizeOption[] = [
  { id: "default", name: "Default", desc: "The standard text size" },
  { id: "large", name: "Large", desc: "A little bigger and easier to read" },
  { id: "larger", name: "Larger", desc: "The biggest, most readable text" },
];

function NameSetting({ playerId }: { playerId: string }) {
  const dataLayer = useContext(DataLayerContext);
  const roster = useObservableProperty(dataLayer.roster);
  const ownRoster = roster?.self.playerId === playerId ? roster : null;
  const [draft, setDraft] = useState<string | null>(null);
  const [loading, setLoading] = useState(!ownRoster);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadName = useCallback(async () => {
    setLoading(true);
    try {
      await dataLayer.loadRoster();
    } catch (err) {
      console.error("Failed to load your name:", err);
    } finally {
      setLoading(false);
    }
  }, [dataLayer]);

  useEffect(() => {
    if (dataLayer.roster.value?.self.playerId !== playerId) {
      void loadName();
    }
  }, [dataLayer, playerId, loadName]);

  const name = draft ?? ownRoster?.self.displayName ?? "";
  const canSave =
    ownRoster !== null &&
    !loading &&
    !saving &&
    name.trim().length > 0 &&
    name.trim() !== ownRoster.self.displayName;

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setMessage("");
    try {
      const result = await dataLayer.updateDisplayName(name);
      if (result === "Success") {
        setDraft(null);
        setMessage("Your name has been saved.");
      } else {
        setMessage(
          result === "NotSignedIn"
            ? "Please sign in again before changing your name."
            : "Your name could not be saved. Please try again.",
        );
      }
    } catch (err) {
      console.error("Failed to save your name:", err);
      setMessage("Your name could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="settings__group settings__name" onSubmit={onSave}>
      <label className="settings__label" htmlFor="display-name">
        Your name
      </label>
      <p className="settings__desc" id="display-name-help">
        Shown for you on your leaderboard and used for new invites. Friends keep any names
        they&apos;ve chosen for you.
      </p>
      <input
        id="display-name"
        className="settings__name-input"
        type="text"
        autoComplete="nickname"
        maxLength={30}
        required
        value={name}
        disabled={loading || saving || !ownRoster}
        aria-describedby="display-name-help display-name-status"
        onChange={(event) => {
          setDraft(event.target.value);
          setMessage("");
        }}
      />
      <button className="btn btn--primary" type="submit" disabled={!canSave}>
        {saving ? "Saving\u2026" : "Save name"}
      </button>
      <div id="display-name-status" className="settings__name-status" role="status">
        {loading ? "Loading your name\u2026" : message}
        {!loading && !ownRoster && (
          <>
            Your name could not be loaded.{" "}
            <button className="settings__name-retry" type="button" onClick={loadName}>
              Try again
            </button>
          </>
        )}
      </div>
    </form>
  );
}

function InstallationChannelSetting({ onSiteResolved }: { onSiteResolved: (site: InstallationSite) => void }) {
  const plugin = installationPlugin();
  const [installation, setInstallation] = useState<InstallationStatus>();
  const [checking, setChecking] = useState(!!plugin);
  const [channelError, setChannelError] = useState("");
  const [switching, setSwitching] = useState(false);
  const site = installation?.site;

  useEffect(() => {
    if (site) onSiteResolved(site);
  }, [site, onSiteResolved]);

  useEffect(() => {
    if (!plugin) return;
    let active = true;
    setChecking(true);
    void plugin.getStatus().then(async (status) => {
      if (!active) return;
      setInstallation(status);
      if (status.verifiedSite === null) {
        const checked = await plugin.check();
        if (active) setInstallation(checked);
      }
    }).catch((error: unknown) => {
      console.error("Installation channel check failed");
      if (active) setChannelError(installationErrorMessage(error));
    }).finally(() => {
      if (active) setChecking(false);
    });
    return () => { active = false; };
  }, [plugin]);

  const checkChannel = async () => {
    if (!plugin || checking) return;
    setChecking(true);
    setChannelError("");
    try {
      setInstallation(await plugin.check());
    } catch (error: unknown) {
      console.error("Installation channel check failed");
      setChannelError(installationErrorMessage(error));
    } finally {
      setChecking(false);
    }
  };

  const switchVersion = async () => {
    if (!plugin || switching) return;
    setSwitching(true);
    setChannelError("");
    try {
      if (installation?.site === "beta") await plugin.switchToProduction();
      else await plugin.switchToBeta();
    } catch {
      console.error("Version switch failed");
      setChannelError("Could not switch versions. Please try again.");
      setSwitching(false);
    }
  };

  if (!plugin) return null;
  return (
    <section className="settings__channel" aria-labelledby="installation-channel-heading">
      <h2 id="installation-channel-heading" className="settings__label">Beta Testing</h2>
      <p className="settings__desc" role="status">
        {checking && installation?.verifiedSite == null ? "Checking beta testing availability..." :
          installation?.site === "beta" ?
            "You are currently using the beta version of Wirder. You can switch back to the non-beta version at any time." :
            installation?.verifiedSite === "beta" ?
              "You are invited to beta test the latest version of Wirder." :
              installation?.verifiedSite === "production" ? "You are using the non-beta version of Wirder." :
                "You can keep using Wirder while beta testing availability is checked."}
      </p>
      {(channelError || installation?.warning) && (
        <p className="settings__name-status" role="alert">{channelError || installation?.warning}</p>
      )}
      {!checking && installation?.verifiedSite == null && (
        <button className="settings__name-retry" onClick={() => void checkChannel()}>
          Check again
        </button>
      )}
      {installation?.verifiedSite === "beta" && (
        <button className="settings__name-retry" disabled={switching} onClick={() => void switchVersion()}>
          {switching ? "Switching..." :
            installation.site === "beta" ? "Switch to non-beta" : "Switch to beta"}
        </button>
      )}
    </section>
  );
}

export function SettingsScreen() {
  const dataLayer = useContext(DataLayerContext);
  const boardFont = useObservableProperty(dataLayer.boardFont);
  const textSize = useObservableProperty(dataLayer.textSize);
  const auth = useObservableProperty(authState);
  const [installationSite, setInstallationSite] = useState<InstallationSite>();

  const onBackClicked = () => {
    dataLayer.navigateBack();
  };

  return (
    <div className="screen settings">
      <AppBar onBack={onBackClicked} />
      <PageHeading
        eyebrow="Preferences"
        title="Settings"
        sub="Personalize how Wirder looks and plays."
      />

      <div className="settings__body">
        {auth.status === "signedIn" && (
          <NameSetting key={auth.identity.playerId} playerId={auth.identity.playerId} />
        )}

        <fieldset className="settings__group">
          <legend className="settings__label">Board font</legend>
          <p className="settings__desc">Pick the typeface for the letters on the play board.</p>

          <div className="fontopts">
            {FONT_OPTIONS.map((opt) => (
              <label
                key={opt.id}
                className={`fontopt${boardFont === opt.id ? " fontopt--on" : ""}`}
              >
                <input
                  type="radio"
                  className="fontopt__input"
                  name="boardFont"
                  value={opt.id}
                  checked={boardFont === opt.id}
                  onChange={() => dataLayer.setBoardFont(opt.id)}
                />
                <span className={`fontopt__preview fontpreview--${opt.id}`} aria-hidden="true">
                  {opt.sample.map((letter, i) => (
                    <span className="fontopt__tile" key={i}>
                      {letter}
                    </span>
                  ))}
                </span>
                <span className="fontopt__meta">
                  <span className="fontopt__name">{opt.name}</span>
                  <span className="fontopt__desc">{opt.desc}</span>
                </span>
                <span className="fontopt__check" aria-hidden="true">
                  <Check />
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="settings__group">
          <legend className="settings__label">Text size</legend>
          <p className="settings__desc">
            Make the text throughout Wirder bigger and easier to read.
          </p>

          <div className="fontopts">
            {TEXT_SIZE_OPTIONS.map((opt) => (
              <label key={opt.id} className={`fontopt${textSize === opt.id ? " fontopt--on" : ""}`}>
                <input
                  type="radio"
                  className="fontopt__input"
                  name="textSize"
                  value={opt.id}
                  checked={textSize === opt.id}
                  onChange={() => dataLayer.setTextSize(opt.id)}
                />
                <span className={`fontopt__preview textpreview--${opt.id}`} aria-hidden="true">
                  Aa
                </span>
                <span className="fontopt__meta">
                  <span className="fontopt__name">{opt.name}</span>
                  <span className="fontopt__desc">{opt.desc}</span>
                </span>
                <span className="fontopt__check" aria-hidden="true">
                  <Check />
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <PremiumAccessCard context="settings" />
        <InstallationChannelSetting onSiteResolved={setInstallationSite} />

        <p className="settings__legal">
          <a
            className="settings__link"
            href="/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </p>

        <div className="settings__version">v{displayedVersion(APP_VERSION, installationSite)}</div>
      </div>
    </div>
  );
}
